import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, FormGrid, QueryState, ErrorState } from '../components/form.js';

interface UserRow {
  id: string;
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
  locale: string;
  isActive: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
}
interface InvitationRow {
  id: string;
  role: string;
  name: string;
  status: string;
  expiresAt: string | null;
  createdAt: string;
}
interface AuditRow {
  id: string;
  action: string;
  entity: string | null;
  entityId: string | null;
  actorId: string | null;
  createdAt: string;
}
interface TranslationRow {
  id: string;
  key: string;
  locale: string;
  value: string;
}
interface BackupRow {
  id: string;
  status: string;
  createdAt: string;
  sizeBytes: number | null;
}

export function Admin() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const isAdmin = user?.role === 'owner_admin';

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{locale === 'fr' ? 'Administration' : 'Admin'}</h1>

      {/* Validate config — available to clinician AND admin */}
      <ValidateConfig />

      {/* Invitations — clinician/admin */}
      <Invitations />

      {!isAdmin ? (
        <Card title="">
          <p className="text-sm text-slate-500">
            {locale === 'fr'
              ? "Les sections utilisateurs, traductions, journal d'audit et sauvegardes sont réservées à l'administrateur."
              : 'User management, translations, audit log and backups are restricted to the administrator.'}
          </p>
        </Card>
      ) : (
        <>
          <Users />
          <Translations />
          <AuditLog />
          <Backups />
        </>
      )}
    </div>
  );
}

function ValidateConfig() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [validatedByName, setName] = useState(user?.name ?? '');
  const [configVersion, setVersion] = useState('1');
  const [notes, setNotes] = useState('');

  const patientQ = useQuery({ queryKey: ['patient'], queryFn: () => api.get<{ validation: { isValidated: boolean; validatedByName: string | null; version: string | null } }>('/patient') });

  const m = useMutation({
    mutationFn: () => api.post('/patient/validate-config', { validatedByName: validatedByName.trim(), configVersion: configVersion.trim(), notes: notes.trim() || undefined }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patient'] }),
  });

  const validated = patientQ.data?.validation.isValidated;

  return (
    <Card title={locale === 'fr' ? 'Validation de la configuration clinique' : 'Clinical configuration validation'}>
      {validated ? (
        <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
          ✓ {locale === 'fr' ? 'Configuration validée par' : 'Configuration validated by'}{' '}
          {patientQ.data?.validation.validatedByName} (v{patientQ.data?.validation.version})
        </p>
      ) : (
        <>
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">⚠️ {t('banner.notValidated')}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              m.mutate();
            }}
          >
            <FormGrid>
              <Field label={locale === 'fr' ? 'Validé par (nom)' : 'Validated by (name)'}>
                <input className={inputClass} value={validatedByName} onChange={(e) => setName(e.target.value)} required />
              </Field>
              <Field label={locale === 'fr' ? 'Version de configuration' : 'Config version'}>
                <input className={inputClass} value={configVersion} onChange={(e) => setVersion(e.target.value)} required />
              </Field>
            </FormGrid>
            <Field label={t('weekly.observations')}>
              <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
            <Button type="submit" disabled={m.isPending}>{t('common.sign')}</Button>
          </form>
        </>
      )}
    </Card>
  );
}

function Invitations() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [role, setRole] = useState<'caregiver' | 'clinician'>('clinician');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['admin', 'invitations'], queryFn: () => api.get<{ invitations: InvitationRow[] }>('/admin/invitations') });

  const createM = useMutation({
    mutationFn: () => api.post<{ inviteUrl: string }>('/admin/invitations', { role, name: name.trim(), email: email.trim() || undefined, locale }),
    onSuccess: (res) => {
      setInviteUrl(res.inviteUrl);
      setName('');
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => api.post(`/admin/invitations/${id}/revoke`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });

  return (
    <Card title={locale === 'fr' ? 'Invitations' : 'Invitations'}>
      <form
        className="flex flex-wrap items-end gap-3 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          createM.mutate();
        }}
      >
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">{t('role.clinician')}</span>
          <Select value={role} onChange={(e) => setRole(e.target.value as 'caregiver' | 'clinician')}>
            <option value="clinician">{t('role.clinician')}</option>
            <option value="caregiver">{t('role.caregiver')}</option>
          </Select>
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Name</span>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Email</span>
          <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <Button type="submit" disabled={createM.isPending}>{locale === 'fr' ? 'Inviter' : 'Invite'}</Button>
      </form>

      {inviteUrl && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm break-all">
          <span className="text-slate-600">{locale === 'fr' ? "Lien d'invitation (affiché une seule fois) :" : 'Invite link (shown once):'}</span>{' '}
          <code className="text-blue-800">{inviteUrl}</code>
        </div>
      )}
      {createM.isError && <div className="mb-3"><ErrorState error={createM.error} fallback={t('common.error')} /></div>}

      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.invitations.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={['Name', t('role.clinician'), 'Status', 'Expires', '']}>
          {q.data?.invitations.map((inv) => (
            <tr key={inv.id} className="border-b border-slate-100">
              <td className="py-2 pr-4">{inv.name}</td>
              <td className="py-2 pr-4">{t(`role.${inv.role}`)}</td>
              <td className="py-2 pr-4"><Badge tone={inv.status === 'accepted' ? 'ok' : inv.status === 'revoked' || inv.status === 'expired' ? 'danger' : 'info'}>{inv.status}</Badge></td>
              <td className="py-2 pr-4 text-xs text-slate-400">{inv.expiresAt?.slice(0, 10)}</td>
              <td className="py-2 pr-4">
                {inv.status === 'pending' && (
                  <button className="text-xs underline text-danger" onClick={() => revokeM.mutate(inv.id)}>{locale === 'fr' ? 'Révoquer' : 'Revoke'}</button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </Card>
  );
}

function Users() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.get<{ users: UserRow[] }>('/admin/users') });
  const m = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/admin/users/${id}`, { isActive }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  // Reset-password panel: which row is open, and the once-shown result.
  const [resetOpenId, setResetOpenId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetDone, setResetDone] = useState<{ id: string; generatedPassword?: string } | null>(null);

  // Edit panel (all editable user info): which row is open + the draft fields.
  const [editOpenId, setEditOpenId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editLocale, setEditLocale] = useState('fr');
  const [editDisableMfa, setEditDisableMfa] = useState(false);
  const editM = useMutation({
    mutationFn: (body: { id: string; name: string; role: string; email: string | null; phone: string | null; locale: string; disableMfa: boolean }) =>
      api.patch(`/admin/users/${body.id}`, { name: body.name, role: body.role, email: body.email, phone: body.phone, locale: body.locale, disableMfa: body.disableMfa || undefined }),
    onSuccess: () => {
      setEditOpenId(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
  function openEdit(u: UserRow) {
    setEditOpenId((cur) => (cur === u.id ? null : u.id));
    setEditName(u.name);
    setEditRole(u.role);
    setEditEmail(u.email ?? '');
    setEditPhone(u.phone ?? '');
    setEditLocale(u.locale || 'fr');
    setEditDisableMfa(false);
    editM.reset();
  }

  const resetM = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword?: string }) =>
      api.post<{ ok: true; generatedPassword?: string }>(`/admin/users/${id}/reset-password`, { newPassword }),
    onSuccess: (res, vars) => {
      setResetDone({ id: vars.id, generatedPassword: res.generatedPassword });
      setResetOpenId(null);
      setResetPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  function openReset(id: string) {
    setResetOpenId((cur) => (cur === id ? null : id));
    setResetPassword('');
    setResetDone(null);
    resetM.reset();
  }

  return (
    <Card title={locale === 'fr' ? 'Utilisateurs' : 'Users'}>
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.users.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={['Name', 'Role', 'Email', 'Phone', 'Active', 'MFA', '']}>
          {q.data?.users.map((u) => {
            // A password is only usable by someone with an email to sign in
            // with — the login route looks users up by email and ignores role,
            // so this mirrors the server's gate exactly.
            const canReset = !!u.email;
            return (
              <tr key={u.id} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4">{u.name}</td>
                <td className="py-2 pr-4">{t(`role.${u.role}`)}</td>
                <td className="py-2 pr-4 text-slate-500">{u.email ?? '—'}</td>
                <td className="py-2 pr-4 text-slate-500 tabular-nums">{u.phone ?? '—'}</td>
                <td className="py-2 pr-4">{u.isActive ? <Badge tone="ok">●</Badge> : <Badge tone="danger">○</Badge>}</td>
                <td className="py-2 pr-4">{u.totpEnabled ? '✓' : '—'}</td>
                <td className="py-2 pr-4">
                  <div className="flex flex-col gap-1 items-start">
                    <div className="flex items-center gap-3">
                      <button className="text-xs underline text-brand" onClick={() => m.mutate({ id: u.id, isActive: !u.isActive })}>
                        {u.isActive ? (locale === 'fr' ? 'Désactiver' : 'Deactivate') : (locale === 'fr' ? 'Activer' : 'Activate')}
                      </button>
                      {canReset ? (
                        <button className="text-xs underline text-brand" onClick={() => openReset(u.id)}>
                          {t('admin.resetPassword')}
                        </button>
                      ) : (
                        // The reason must be VISIBLE, not hidden in a hover
                        // title — a disabled control with no stated cause reads
                        // as a broken app, and hover does not exist on touch.
                        <span className="text-xs text-slate-400">
                          {t('admin.resetPassword')}
                          <span className="ml-1 text-slate-500">— {t('admin.cannotResetNoEmail')}</span>
                        </span>
                      )}
                      <button className="text-xs underline text-brand" onClick={() => openEdit(u)}>
                        {locale === 'fr' ? 'Modifier' : 'Edit'}
                      </button>
                    </div>

                    {editOpenId === u.id && (
                      <form
                        className="flex flex-col gap-2 mt-1 bg-slate-50 border border-slate-200 rounded px-3 py-2 w-full max-w-md"
                        onSubmit={(e) => {
                          e.preventDefault();
                          editM.mutate({ id: u.id, name: editName.trim(), role: editRole, email: editEmail.trim() || null, phone: editPhone.trim() || null, locale: editLocale, disableMfa: editDisableMfa });
                        }}
                      >
                        <div className="text-xs font-medium text-slate-600">
                          {locale === 'fr' ? `Modifier ${u.name}` : `Edit ${u.name}`}
                        </div>
                        <label className="text-xs text-slate-500">
                          {locale === 'fr' ? 'Nom' : 'Name'}
                          <input className={inputClass} value={editName} onChange={(e) => setEditName(e.target.value)} required />
                        </label>
                        <label className="text-xs text-slate-500">
                          {locale === 'fr' ? 'Type de compte' : 'Account type'}
                          <Select value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                            {ROLES.map((r) => (
                              <option key={r} value={r}>{t(`role.${r}`)}</option>
                            ))}
                          </Select>
                        </label>
                        {/* Email is the login for clinician/admin; phone is the login for a parent. */}
                        <label className="text-xs text-slate-500">
                          {locale === 'fr' ? 'E-mail (médecin/admin)' : 'Email (clinician/admin)'}
                          <input className={inputClass} type="email" autoComplete="off" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                        </label>
                        <label className="text-xs text-slate-500">
                          {locale === 'fr' ? 'Téléphone (parent)' : 'Phone (parent)'}
                          <input className={inputClass} type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="+225…" />
                        </label>
                        <label className="text-xs text-slate-500">
                          {locale === 'fr' ? 'Langue' : 'Language'}
                          <Select value={editLocale} onChange={(e) => setEditLocale(e.target.value)}>
                            <option value="fr">Français</option>
                            <option value="en">English</option>
                          </Select>
                        </label>
                        {u.totpEnabled && (
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input type="checkbox" checked={editDisableMfa} onChange={(e) => setEditDisableMfa(e.target.checked)} />
                            {locale === 'fr' ? 'Désactiver la double authentification (perte d’accès)' : 'Disable MFA (lost authenticator)'}
                          </label>
                        )}
                        <div className="flex items-center gap-2">
                          <Button type="submit" disabled={editM.isPending}>{t('common.save')}</Button>
                          <button type="button" className="text-xs underline text-slate-500" onClick={() => setEditOpenId(null)}>
                            {t('common.cancel')}
                          </button>
                        </div>
                        {editM.isError && <ErrorState error={editM.error} fallback={t('common.error')} />}
                      </form>
                    )}

                    {canReset && resetOpenId === u.id && (
                      <form
                        className="flex flex-wrap items-end gap-2 mt-1 bg-slate-50 border border-slate-200 rounded px-3 py-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          resetM.mutate({ id: u.id, newPassword: resetPassword || undefined });
                        }}
                      >
                        <div className="w-full text-xs font-medium text-slate-600">{t('admin.resetPasswordFor', { name: u.name })}</div>
                        <input
                          type="password"
                          className={inputClass}
                          placeholder={t('admin.newPasswordOptional')}
                          minLength={resetPassword ? 12 : undefined}
                          autoComplete="new-password"
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                        />
                        <Button type="submit" disabled={resetM.isPending}>{t('admin.resetPassword')}</Button>
                        <button type="button" className="text-xs underline text-slate-500" onClick={() => openReset(u.id)}>
                          {t('common.cancel')}
                        </button>
                      </form>
                    )}

                    {resetDone?.id === u.id && (
                      resetDone.generatedPassword ? (
                        <div className="mt-1 bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm break-all">
                          <span className="text-slate-600">{t('admin.tempPasswordOnce')}</span>{' '}
                          <code className="text-blue-800">{resetDone.generatedPassword}</code>
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
                          {t('admin.passwordReset')}
                        </div>
                      )
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>
      </QueryState>
      {m.isError && <div className="mt-2"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
      {resetM.isError && <div className="mt-2"><ErrorState error={resetM.error} fallback={t('common.error')} /></div>}
    </Card>
  );
}

function Translations() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'translations'], queryFn: () => api.get<{ translations: TranslationRow[] }>('/admin/translations') });
  const [key, setKey] = useState('');
  const [tlLocale, setTlLocale] = useState<'fr' | 'en'>('fr');
  const [value, setValue] = useState('');

  const m = useMutation({
    mutationFn: () => api.put('/admin/translations', { key: key.trim(), locale: tlLocale, value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'translations'] });
      setKey('');
      setValue('');
    },
  });

  return (
    <Card title={locale === 'fr' ? 'Traductions (surcharges)' : 'Translations (overrides)'}>
      <form
        className="flex flex-wrap items-end gap-3 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Key</span>
          <input className={inputClass} value={key} onChange={(e) => setKey(e.target.value)} placeholder="action.contactPediatrician" required />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Locale</span>
          <Select value={tlLocale} onChange={(e) => setTlLocale(e.target.value as 'fr' | 'en')}>
            <option value="fr">FR</option>
            <option value="en">EN</option>
          </Select>
        </label>
        <label className="text-sm flex-1 min-w-48">
          <span className="block text-slate-600 mb-1">Value</span>
          <input className={inputClass} value={value} onChange={(e) => setValue(e.target.value)} required />
        </label>
        <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
      </form>
      {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}

      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.translations.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={['Key', 'Locale', 'Value']}>
          {q.data?.translations.map((tr) => (
            <tr key={tr.id} className="border-b border-slate-100">
              <td className="py-2 pr-4 font-mono text-xs">{tr.key}</td>
              <td className="py-2 pr-4">{tr.locale.toUpperCase()}</td>
              <td className="py-2 pr-4">{tr.value}</td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </Card>
  );
}

function AuditLog() {
  const { t, locale } = useI18n();
  const q = useQuery({ queryKey: ['admin', 'audit'], queryFn: () => api.get<{ audit: AuditRow[] }>('/admin/audit?limit=100') });
  return (
    <Card title={locale === 'fr' ? "Journal d'audit" : 'Audit log'}>
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.audit.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={['When', 'Action', 'Entity', 'Actor']}>
          {q.data?.audit.map((a) => (
            <tr key={a.id} className="border-b border-slate-100">
              <td className="py-1 pr-4 text-xs text-slate-400 whitespace-nowrap">{a.createdAt?.slice(0, 19).replace('T', ' ')}</td>
              <td className="py-1 pr-4"><Badge tone="info">{a.action}</Badge></td>
              <td className="py-1 pr-4 text-slate-600">{a.entity ?? '—'}</td>
              <td className="py-1 pr-4 text-xs text-slate-400">{a.actorId?.slice(0, 8) ?? '—'}</td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </Card>
  );
}

function Backups() {
  const { t, locale } = useI18n();
  const q = useQuery({ queryKey: ['admin', 'backups'], queryFn: () => api.get<{ backups: BackupRow[] }>('/admin/backups') });
  return (
    <Card title={locale === 'fr' ? 'Sauvegardes' : 'Backups'}>
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.backups.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={['When', 'Status', 'Size']}>
          {q.data?.backups.map((b) => (
            <tr key={b.id} className="border-b border-slate-100">
              <td className="py-1 pr-4 text-xs text-slate-400">{b.createdAt?.slice(0, 19).replace('T', ' ')}</td>
              <td className="py-1 pr-4"><Badge tone={b.status === 'completed' ? 'ok' : b.status === 'failed' ? 'danger' : 'info'}>{b.status}</Badge></td>
              <td className="py-1 pr-4 text-slate-500">{b.sizeBytes != null ? `${(b.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '—'}</td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </Card>
  );
}
